import React from 'react';
import { useRouter } from 'expo-router';
import { useTranslation } from '@/hooks/useTranslation';
import NavigationBarButtonContainer from '@/components/Navigation/NavigationBarButtonContainer';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';
import ThemeSelector from '@/components/Navigation/ThemeSelector';
import InfoSelector from '@/components/Navigation/InfoSelector';
import useResponsiveness from '@/hooks/useResponsiveness';
import { isArtifactNavigationActive } from '@/constants/config';
import { useUIStore } from '@/store';
import { useAuthPresentation } from '@/hooks/useRequireAuth';
import { Keyboard, Platform } from 'react-native';
import { useNavigation, DrawerActions } from '@react-navigation/native';

export default function DefaultRouteNavigationHeaderLeft({
  children,
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const toggleSidebar = useUIStore(state => state.toggleSidebar);
  const navigation = useNavigation();
  const { isXS } = useResponsiveness();
  const hasCompactNavigation = useUIStore(state =>
    isArtifactNavigationActive(state) && !state.artifactNavigationExpanded
  ) && !isXS;

  // Match the optimistic Android shell while persisted auth hydrates or its
  // access token refreshes. Otherwise the shell shows signed-in content with
  // the logged-out home/theme/help controls during a cold start.
  const isAuthenticated = useAuthPresentation();
  // Web app layout always mounts AppSidebar (including logged-out / ghost-friendly flows);
  // expose the same hamburger as authenticated users so narrow viewports can open it.
  // The compact rail's logo already opens the sidebar.
  const showSidebarToggle = (isAuthenticated || Platform.OS === 'web') && !hasCompactNavigation;

  const handleMenuPress = () => {
    if (Platform.OS === 'android' || Platform.OS === 'ios') {
      Keyboard.dismiss();
    }

    if (Platform.OS === 'web' || Platform.OS === 'macos') {
      toggleSidebar();
      return;
    }

    // Prefer dispatching DrawerActions for reliability on native
    try {
      navigation.dispatch(DrawerActions.toggleDrawer());
      return;
    } catch (e) {}

    if (navigation && typeof navigation.openDrawer === 'function') {
      navigation.openDrawer();
      return;
    }

    if (navigation && typeof navigation.toggleDrawer === 'function') {
      navigation.toggleDrawer();
      return;
    }

    // Fallback
    toggleSidebar();
  };

  return (
    <NavigationBarButtonContainer leftPadding>
      {showSidebarToggle && (
        <NavigationBarButton
          icon="bars-sort"
          tooltip={t('nav.routes.defaultHeader.toggleSidebar')}
          tooltipShortcut='⌘ ⇧ S'
          onPress={handleMenuPress}
        />
      )}
      {!isAuthenticated && Platform.OS !== 'web' && (
        <NavigationBarButton
          icon="home"
          title={t('navigation.home')}
          onPress={() => router.push('/')}
        />
      )}
      {/* On web the theme + info controls live in the sidebar header for
          logged-out visitors; native keeps them inline since there's no
          persistent sidebar header. */}
      {!isAuthenticated && Platform.OS !== 'web' && <ThemeSelector showAccentSelector={false} />}
      {!isAuthenticated && Platform.OS !== 'web' && <InfoSelector />}
      {children}
    </NavigationBarButtonContainer>
  );
}
